import React from 'react';
import { useObjectivesList } from '../../../../hooks/useObjectivesList';
import {
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { useContentContext } from '../../context/content-context';

/**
 * @fileoverview Componente selector de pasos para widgets del Panel Dinámico.
 * @module SelectStep
 */

/**
 * Componente SelectStep.
 *
 * Permite al usuario seleccionar un paso específico de un objetivo monitoreado
 * para filtrar o visualizar datos asociados a ese paso en el widget correspondiente.
 *
 * Funcionalidad:
 * - Obtiene la lista de objetivos y sus pasos.
 * - Filtra los pasos correspondientes al `objetivoId` del widget actual (slot).
 * - Renderiza un menú desplegable con los pasos disponibles.
 * - Al seleccionar un paso, actualiza la propiedad `customProperties.pasoId` del widget
 *   en el estado global, lo que dispara una actualización del gráfico o contenido.
 *
 * @returns {React.ReactElement|null} Selector de pasos o null si no hay pasos.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function SelectStep() {
  const { t } = useTranslation();
  const {
    data: objectives,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const { slot } = useContentContext();

  /**
   * Lista de pasos asociados al objetivo configurado en el widget actual.
   * Se deriva de la lista completa de objetivos buscando por `slot.objetivoId`.
   * Memorizado para evitar recálculos innecesarios en cada renderizado.
   * @type {Array<Object>|undefined}
   */
  const pasosObjetivo = React.useMemo(() => {
    return objectives?.find((obj) => obj.objetivo_id === slot.objetivoId)
      ?.Pasos;
  }, [objectives, slot.objetivoId]);

  /**
   * Maneja el cambio de selección de paso.
   *
   * Actualiza el layout global asignando el nuevo `pasoId` a las propiedades
   * personalizadas del widget actual.
   *
   * @param {Object} event - Evento de cambio del select.
   * @param {number} event.target.value - ID del paso seleccionado.
   */
  function handleChange(event) {
    const value = event.target.value;
    const newLayout = [...state.activeLayout].map((item) => {
      if (item.id === slot.id) {
        return {
          ...item,
          customProperties: { ...(item.customProperties ?? {}), pasoId: value },
        };
      }
      return item;
    });

    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: newLayout,
    });
  }

  return (
    <>
      {pasosObjetivo ? (
        <FormControl fullWidth value={slot.customProperties?.pasoId || 0}>
          <InputLabel id="select-paso-label">
            {t('dynamicPanel.selectStep.step')}
          </InputLabel>
          <Select
            labelId="select-paso-label"
            disabled={isLoadingObjectiveList}
            displayEmpty
            onChange={handleChange}
            label={t('dynamicPanel.selectStep.step')}
            value={slot.customProperties?.pasoId || 0}
          >
            {pasosObjetivo.map((paso) => (
              <MenuItem key={paso.id_paso} value={paso.id_paso}>
                {paso.nombre_paso}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <></>
      )}
      {isLoadingObjectiveList && <LinearProgress />}
    </>
  );
  return null;
}
